import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import './BaseModal.css';

export interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  className?: string;
}

/**
 * BaseModal - Common modal structure with overlay and accessibility
 * 
 * Provides consistent modal wrapper with:
 * - Backdrop overlay with click-outside to close
 * - ESC key handler to close
 * - Focus trap for accessibility
 * - Close button
 * - Header, body sections
 * 
 * @param isOpen - Whether the modal is open
 * @param onClose - Callback to close the modal
 * @param children - Modal content
 * @param title - Optional modal title
 * @param className - Optional additional CSS class
 */
export const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  className,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Handle backdrop click to close modal
  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div 
      className="topBar-modal-overlay" 
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div 
        className={`topBar-modal-content ${className || ''}`}
        ref={modalRef}
      >
        <div className="topBar-modal-header">
          {title && (
            <h2 id="modal-title" className="topBar-modal-title">
              {title}
            </h2>
          )}
          <button
            className="topBar-modal-close"
            onClick={onClose}
            aria-label="Close modal"
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="topBar-modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

export default BaseModal;

